import { useEffect, useState } from 'react';
import type { AddressInput } from '@ecom/contracts';
import { AsyncSelect } from '@/components/async-select';
import { Field, Input, Textarea } from '@/components/ui';
import { api, withSession } from '@/lib/api-client';
import { cityOptions, regionOptions } from '@/lib/options';
import { useT } from '@/i18n/use-t';

/**
 * A delivery address, as checkout takes it (R5): the recipient, the street,
 * the region and city from the geography — searched, never listed whole — and
 * the Saudi national address beside. Used wherever staff write one on an order
 * (ADR-062), so the two screens cannot ask for it differently.
 */

export const EMPTY_ADDRESS: AddressInput = {
  fullName: '',
  phone: '',
  line1: '',
  city: '',
  region: '',
  country: 'SA',
};

/** Enough to price delivery and reach the door. */
export function addressComplete(address: AddressInput): boolean {
  return Boolean(address.fullName.trim() && address.phone.trim() && address.line1.trim() && address.cityId && address.regionId);
}

export function AddressFields({
  value,
  onChange,
  idPrefix = 'address',
}: {
  value: AddressInput;
  onChange: (next: AddressInput) => void;
  idPrefix?: string;
}) {
  const t = useT();
  const set = (patch: Partial<AddressInput>) => onChange({ ...value, ...patch });

  // The home country, once: regions hang off it. One market today; a second
  // would choose here rather than fetch everything.
  const [countryId, setCountryId] = useState('');
  useEffect(() => {
    let cancelled = false;
    void withSession(() => api.geography.countries({ limit: 1 }))
      .then((page) => {
        if (!cancelled && page.items[0]) setCountryId(page.items[0].id);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // The order and the words of the order page's address correction, so the
  // two read alike; region and city are added, because they price delivery.
  return (
    <div className="flex flex-col gap-4">
      <Field label={t('fullName')} htmlFor={`${idPrefix}-name`}>
        <Input
          id={`${idPrefix}-name`}
          value={value.fullName}
          maxLength={120}
          onChange={(event) => set({ fullName: event.target.value })}
        />
      </Field>
      <Field label={t('addressPhone')} htmlFor={`${idPrefix}-phone`}>
        <Input
          id={`${idPrefix}-phone`}
          dir="ltr"
          inputMode="tel"
          value={value.phone}
          maxLength={30}
          onChange={(event) => set({ phone: event.target.value })}
        />
      </Field>
      <Field label={t('addressLine1')} htmlFor={`${idPrefix}-line1`}>
        <Input id={`${idPrefix}-line1`} value={value.line1} maxLength={200} onChange={(event) => set({ line1: event.target.value })} />
      </Field>
      <Field label={t('addressLine2')} htmlFor={`${idPrefix}-line2`} hint={t('optional')}>
        <Input
          id={`${idPrefix}-line2`}
          value={value.line2 ?? ''}
          maxLength={200}
          onChange={(event) => set({ line2: event.target.value || undefined })}
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('region')} htmlFor={`${idPrefix}-region`}>
          <AsyncSelect
            id={`${idPrefix}-region`}
            value={value.regionId ?? ''}
            emptyLabel={value.region || t('choose')}
            allowClear={false}
            fetchPage={(search, cursor) => regionOptions(countryId).page(search, cursor)}
            // A new region empties the city: a city belongs to one region.
            onChange={(id, option) => set({ regionId: id || undefined, region: option?.label ?? '', cityId: undefined, city: '' })}
          />
        </Field>
        <Field label={t('city')} htmlFor={`${idPrefix}-city`}>
          <AsyncSelect
            id={`${idPrefix}-city`}
            value={value.cityId ?? ''}
            emptyLabel={value.city || t('choose')}
            allowClear={false}
            disabled={!value.regionId}
            fetchPage={(search, cursor) => cityOptions(value.regionId ?? '').page(search, cursor)}
            onChange={(id, option) => set({ cityId: id || undefined, city: option?.label ?? '' })}
          />
        </Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('addressDistrict')} htmlFor={`${idPrefix}-district`} hint={t('optional')}>
          <Input
            id={`${idPrefix}-district`}
            value={value.district ?? ''}
            maxLength={120}
            onChange={(event) => set({ district: event.target.value || undefined })}
          />
        </Field>
        <Field label={t('addressShortCode')} htmlFor={`${idPrefix}-short`} hint={t('addressShortCodeHint')}>
          <Input
            id={`${idPrefix}-short`}
            dir="ltr"
            value={value.shortCode ?? ''}
            maxLength={16}
            onChange={(event) => set({ shortCode: event.target.value || undefined })}
          />
        </Field>
      </div>
      <Field label={t('addressPostalCode')} htmlFor={`${idPrefix}-postal`} hint={t('optional')}>
        <Input
          id={`${idPrefix}-postal`}
          dir="ltr"
          value={value.postalCode ?? ''}
          maxLength={20}
          onChange={(event) => set({ postalCode: event.target.value || undefined })}
        />
      </Field>
      <Field label={t('addressDirections')} htmlFor={`${idPrefix}-directions`} hint={t('optional')}>
        <Textarea
          id={`${idPrefix}-directions`}
          rows={2}
          value={value.directions ?? ''}
          maxLength={300}
          onChange={(event) => set({ directions: event.target.value || undefined })}
        />
      </Field>
    </div>
  );
}
